/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { createContext, use, useMemo, useSyncExternalStore } from 'react';
import type { ShopperProducts } from '@/scapi';

/**
 * A lightweight store for enriched subcategory data. The store instance is referentially stable so the context value
 * never changes, which means the context provider never triggers re-renders of its consumer tree. Instead, only
 * components that subscribe via {@link useSubCategory} re-render when the store is updated.
 */
export type SubCategoryStore = ReturnType<typeof createSubCategoryStore>;

export function createSubCategoryStore() {
    let data = new Map<string, ShopperProducts.schemas['Category']>();
    const listeners = new Set<() => void>();

    return {
        update(this: void, entries: Map<string, ShopperProducts.schemas['Category']>): void {
            data = entries;
            listeners.forEach((l) => l());
        },
        subscribe(this: void, listener: () => void): () => void {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
        getSnapshot(this: void): Map<string, ShopperProducts.schemas['Category']> {
            return data;
        },
    };
}

export const SubCategoryContext = createContext<SubCategoryStore>(createSubCategoryStore());

/**
 * Hook that subscribes to the {@link SubCategoryStore} via context and returns the enriched category for the given ID,
 * or `undefined` if not yet available. Re-renders only when the entry for the given `id` changes identity,
 * not on every store update or context value change.
 */
export function useSubCategory(id: string): ShopperProducts.schemas['Category'] | undefined {
    const store = use(SubCategoryContext);
    return useSyncExternalStore(
        store.subscribe,
        () => store.getSnapshot().get(id),
        () => store.getSnapshot().get(id)
    );
}

/**
 * Returns whether deferred subcategory data has been loaded into the store.
 * Useful for showing skeleton placeholders while deeper category trees stream in.
 */
export function useSubCategoriesLoaded(): boolean {
    const store = use(SubCategoryContext);
    return useSyncExternalStore(
        store.subscribe,
        () => store.getSnapshot().size > 0,
        () => store.getSnapshot().size > 0
    );
}

/**
 * Merges root-level categories with deferred subcategory data in a single store subscription.
 * Prefer this over per-item {@link useSubCategory} when rendering lists — one re-render when defer
 * resolves instead of one re-render per menu item.
 */
export function useEnrichedCategories(
    rootCategories: ShopperProducts.schemas['Category'][]
): ShopperProducts.schemas['Category'][] {
    const store = use(SubCategoryContext);
    const deferredById = useSyncExternalStore(
        store.subscribe,
        () => store.getSnapshot(),
        () => store.getSnapshot()
    );

    return useMemo(
        () =>
            rootCategories.map((category) => {
                const enriched = deferredById.get(category.id);
                return enriched ?? category;
            }),
        [rootCategories, deferredById]
    );
}
